/**
 * ClipboardPaste hook for intelligently reading clipboard content
 * Supports text and images, rejects unsupported file types
 */
const ClipboardPaste = {
  mounted() {
    this.el.addEventListener('click', async (e) => {
      e.preventDefault();

      try {
        // Read all clipboard items to check their types
        const clipboardItems = await navigator.clipboard.read();

        if (!clipboardItems || clipboardItems.length === 0) {
          this.pushEvent('flash', {
            type: 'warning',
            message: 'Clipboard is empty'
          });
          return;
        }

        // Process the first clipboard item
        const item = clipboardItems[0];
        const types = item.types;

        // Define supported image types
        const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

        // Check for various content types
        const hasImage = types.some(type => imageTypes.includes(type));
        const hasText = types.includes('text/plain') || types.includes('text/html');

        // Check for file/URI list types (indicates file copy, not plain text)
        const hasFileReference = types.some(type =>
          type.includes('Files') ||
          type === 'text/uri-list' ||
          type.startsWith('application/x-') // System-specific file types
        );

        // Check for unsupported file types (BEFORE checking text, since files include text/plain for filename)
        const hasUnsupportedFiles = types.some(type => {
          // Reject application types (PDFs, ZIPs, etc.), videos, audio
          return type.startsWith('application/') ||
                 type.startsWith('video/') ||
                 type.startsWith('audio/') ||
                 (type.startsWith('image/') && !imageTypes.includes(type));
        });

        // If there are file references or unsupported files, reject even if text is present
        if (hasFileReference || hasUnsupportedFiles) {
          // Special case: if it's an image file, allow it
          if (hasImage && !hasUnsupportedFiles) {
            // Continue to image handling below
          } else {
            this.pushEvent('flash', {
              type: 'error',
              message: 'Unsupported file type in clipboard. Only text and images are supported.'
            });
            return;
          }
        }

        // Handle images (prioritize images over text if both are present)
        if (hasImage) {
          const imageType = types.find(type => imageTypes.includes(type));
          const blob = await item.getType(imageType);

          // CLIENT-SIDE SIZE VALIDATION
          // Get max size from data attribute or use default (10 MB)
          const maxSize = parseInt(this.el.dataset.maxSize || '10000000', 10);

          if (blob.size > maxSize) {
            // Format size for user-friendly message
            const sizeMB = (blob.size / 1_000_000).toFixed(1);
            const maxSizeMB = (maxSize / 1_000_000).toFixed(1);

            this.pushEvent('flash', {
              type: 'error',
              message: `Image is too large (${sizeMB} MB). Maximum size is ${maxSizeMB} MB.`
            });
            return;
          }

          // Generate filename with timestamp
          const filename = `clipboard-image-${Date.now()}.${imageType.split('/')[1]}`;

          // Convert Blob to File
          const file = new File([blob], filename, { type: imageType });

          // Add to LiveView upload system
          // Use data-phx-upload-ref to find the file input (more reliable than ID)
          const fileInput = document.querySelector('input[type="file"][data-phx-upload-ref]');
          if (!fileInput) {
            console.error('File upload input not found in DOM');
            this.pushEvent('flash', {
              type: 'error',
              message: 'File upload system not available'
            });
            return;
          }

          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          fileInput.files = dataTransfer.files;

          // Trigger change event to notify LiveView
          fileInput.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Handle text (only if no file references detected)
        if (hasText) {
          // Try to get plain text first, fall back to HTML
          const textType = types.includes('text/plain') ? 'text/plain' : 'text/html';
          const blob = await item.getType(textType);
          const text = await blob.text();

          if (text && text.trim()) {
            this.pushEvent('add_clipboard_text', { text: text });
          } else {
            this.pushEvent('flash', {
              type: 'warning',
              message: 'Clipboard text is empty'
            });
          }
          return;
        }

        // If we get here, clipboard has unknown content
        this.pushEvent('flash', {
          type: 'warning',
          message: 'No supported content found in clipboard'
        });

      } catch (err) {
        // Handle permission denied or other errors
        console.error('Clipboard read error:', err);

        // Provide a more helpful error message
        let errorMessage = 'Failed to read clipboard';
        if (err.name === 'NotAllowedError') {
          errorMessage = 'Clipboard access denied. Please grant permission and try again.';
        } else if (err.message) {
          errorMessage = `Clipboard error: ${err.message}`;
        }

        this.pushEvent('flash', {
          type: 'error',
          message: errorMessage
        });
      }
    });
  }
};

export default ClipboardPaste;
